import React, { ReactNode } from "react";
import { OutlinedInputProps } from "@mui/material";
import type { PicklistEntity } from "../../functions/picklistValue";
import { PhaseWithTasks, ProjectIndividual, ProjectTask } from "./canonical";
import { IncludeExcludeFilterCategory } from "../../components/include-exclude-filter/reducers/includeExcludeFilterReducer";
import { IncludeExcludeFilterType } from "../../components/include-exclude-filter/IncludeExcludeFilter";
import { ContractDto, ExtendedContract } from "../contracts-feature/canonical";

export enum DetailFieldType {
  Text,
  Number,
  Date,
}

export interface DetailField<T> {
  fieldKey: keyof T;
  label: string;
  type: DetailFieldType;
  columnKey: number;
  sortPriority: number;
  inputProps?: OutlinedInputProps;
  isPicklist?: boolean;
  picklistEntityKey?: string;
  picklistFieldKey?: string;
  disabled?: boolean;
}

export interface RecordsDetail {
  title: string;
  count: number;
  linkTo: string;
  contractName: string;
  contractId?: number;
}

export interface ContractUseLocationState {
  state?: ContractUseLocationState;
  includeChildren?: boolean;
  title?: string;
  subtitle?: string;
  backButtonAction?: () => void;
  contractID?: string;
}

export interface ContractDetailField extends DetailField<ContractDto> {}

export type ProjectDuration = {
  duration: number;
  currentProgress: number;
};

export interface ProjectDetailsPhaseProps {
  phasesWithTasks: PhaseWithTasks[];
  orphanTasks: ProjectTask[];
  picklist: PicklistEntity[] | undefined;
}

export interface ProjectDetailChildPhaseProps {
  childPhases: PhaseWithTasks;
  displayTasks: (
    tasks: ProjectTask[],
    style?: React.CSSProperties,
  ) => React.JSX.Element[];
  picklist: PicklistEntity[] | undefined;
  style: React.CSSProperties;
}

export interface ProjectDetailsPageHeaderProps {
  children?: React.ReactNode;
  picklist: PicklistEntity[] | undefined;
  selectedProject: ProjectIndividual;
}

export interface ProjectDetailsPhaseStepperProps {
  phasesWithTasks: PhaseWithTasks[];
  activePhase: number;
}

export interface ProjectDetailsTimelineCardProps {
  selectedProject: ProjectIndividual;
  duration: ProjectDuration | undefined;
  timelineProgress: number;
}

export interface ProjectDetailsMetadataProps {
  selectedProject: ProjectIndividual;
  openContractDetailDialog: () => void;
  contract: ContractDto | undefined;
}

export interface ContractDetailsDialogProps {
  open: boolean;
  onClose: () => void;
  contract: ContractDto;
  picklist: PicklistEntity[] | undefined;
}

export interface ContractDetailsDialogContainerProps {
  open: boolean;
  onClose: () => void;
  children: ReactNode;
}

export interface ContractDetailsDialogCloseOnlyProps {
  onClose: () => void;
}

export interface ContractDetailsDialogContentProps {
  loading: boolean;
  recordsDetail: RecordsDetail[];
  contract: ContractDto;
  picklist: PicklistEntity[] | undefined;
}

export interface ServiceContractDetailsContainerProps {
  contract: ExtendedContract;
  picklist: PicklistEntity[] | undefined;
}

export interface ContractDetailTextFieldProps {
  field: ContractDetailField;
  contract: ContractDto;
  picklist: PicklistEntity[] | undefined;
}

export interface RecordsDetailContainerProps {
  recordsDetail: RecordsDetail[];
  loading: boolean;
}

export interface RecordsDetailItemProps {
  title: string;
  count: number;
  linkTo: string;
}

/**
 * FilterSortGroup type for project detail filtering, sorting, and grouping controls
 * Used by ProjectDetailsGridWidgets and ProjectDetailsGroupColumns
 */
export interface FilterSortGroup {
  filters: {
    categories: IncludeExcludeFilterCategory[];
    id?: string | null;
    currentFilters: IncludeExcludeFilterType | null;
  };
  sort: { id: string; label: string } | null;
  grouping: {
    id?: string | null;
    group: { id: string; label: string } | null;
    groupSort: { id: string; label: string } | null;
  };
}

/**
 * Default sort configuration for project tasks
 */
export const DEFAULT_SORT = { id: "status", label: "Status" } as const;

/**
 * Default grouping configuration for project tasks
 */
export const DEFAULT_GROUPING = {
  id: "phase-date",
  group: { id: "group-phase", label: "Phase" },
  groupSort: { id: "sort-start-date", label: "Start Date" },
} as const;
